import { addEntity, addComponent } from 'bitecs';
import { Position, Animal, AI, AI_STATE, ANIMAL_TYPES, Sprite } from '../world.js';
import { Sprite as PixiSprite, Texture } from 'pixi.js';
import { TILE_SIZE } from '../config.js';
import { Graphics } from 'pixi.js';
import { getAIStateName } from '../systems/animals.js';

let app = null;
let beeEaterTexture = null;

export function setApp(pixiApp) {
    app = pixiApp;
    // Preload the texture when app is set
    if (app && app.assetManager) {
        console.log('[BEE_EATER] Preloading bee-eater texture...');
        app.assetManager.sprites.get('bee_eater').then(texture => {
            beeEaterTexture = texture;
            console.log('[BEE_EATER] Texture preloaded successfully');
        }).catch(error => {
            console.error('[BEE_EATER] Failed to preload texture:', error);
        });
    }
}

export function createBeeEater(world, x, y) {
    const entity = addEntity(world);
    
    // Add components
    addComponent(world, Position, entity);
    addComponent(world, Animal, entity);
    addComponent(world, AI, entity);
    addComponent(world, Sprite, entity);
    
    // Initialize Position
    Position.x[entity] = x;
    Position.y[entity] = y;
    
    // Initialize Animal
    Animal.type[entity] = ANIMAL_TYPES.BEE_EATER.id;
    Animal.age[entity] = 0;
    Animal.weight[entity] = ANIMAL_TYPES.BEE_EATER.maxWeight;
    Animal.isMoving[entity] = false;
    Animal.energy[entity] = ANIMAL_TYPES.BEE_EATER.maxEnergy * 0.3; // Start with 30% energy
    Animal.maxEnergy[entity] = ANIMAL_TYPES.BEE_EATER.maxEnergy;
    Animal.lastEnergyUpdate[entity] = 0;
    Animal.fleeTimer[entity] = 0;
    Animal.isFleeing[entity] = false;
    
    // Initialize AI
    AI.state[entity] = AI_STATE.HUNTING;  // Start in HUNTING state since energy is low
    console.log('[BEE_EATER] Initial AI state set to:', getAIStateName(AI.state[entity]));
    
    const randomAngle = Math.random() * Math.PI * 2;
    const randomDistance = Math.random() * 100;  // Random distance between 0 and 100
    AI.targetX[entity] = x + Math.cos(randomAngle) * randomDistance;
    AI.targetY[entity] = y + Math.sin(randomAngle) * randomDistance;
    AI.speed[entity] = ANIMAL_TYPES.BEE_EATER.speed;
    AI.timer[entity] = 0;
    AI.changeDirectionTimer[entity] = 5.0;  // 5 seconds before changing direction
    AI.changeDirectionInterval[entity] = 5; // Change direction every 5 seconds
    
    console.log(`[BEE_EATER] Created with speed: ${AI.speed[entity]}, base speed from type: ${ANIMAL_TYPES.BEE_EATER.speed}`);
    
    // Create sprite using preloaded texture
    if (!app || !app.worldContainer) {
        console.warn('[BEE_EATER] No app or worldContainer available - sprite will be created later');
        return entity;
    }
    
    try {
        let sprite;
        if (beeEaterTexture) {
            // Use preloaded texture
            sprite = new PixiSprite(beeEaterTexture);
            console.log('[BEE_EATER] Using preloaded texture for sprite');
        } else {
            // Create fallback sprite if texture isn't loaded yet
            console.warn('[BEE_EATER] No preloaded texture available, using fallback');
            const fallbackGraphics = new Graphics();
            fallbackGraphics.beginFill(0xFF0000); // Red color for visibility
            fallbackGraphics.drawCircle(0, 0, TILE_SIZE * 0.25); // Draw a circle
            fallbackGraphics.endFill();
            
            const fallbackTexture = app.renderer.generateTexture(fallbackGraphics);
            sprite = new PixiSprite(fallbackTexture);
            fallbackGraphics.destroy();
        }
        
        sprite.anchor.set(0.5);
        sprite.position.set(x, y);
        sprite.scale.set(0.5); // Adjust size as needed
        
        app.worldContainer.addChild(sprite);
        world.sprites[entity] = sprite;
        
        // Add update function to handle sprite flipping
        if (!world.spriteUpdates) world.spriteUpdates = {};
        world.spriteUpdates[entity] = () => {
            const dx = AI.targetX[entity] - Position.x[entity];
            if (dx !== 0) {
                sprite.scale.x = Math.sign(dx) * 0.5; // Use the same scale as set above
            }
        };
        
    } catch (error) {
        console.error('[BEE_EATER] Error creating sprite:', error);
    }
    
    return entity;
}

// Helper function to find the nearest target animal based on prey type
export function findNearestTargetAnimal(world, predatorX, predatorY, preyTypeId) {
    let nearestTarget = null;
    let minDistance = Infinity;
    const MAX_SEARCH_DISTANCE = TILE_SIZE * 50; // Look within 50 tiles
    const preyTypeName = Object.keys(ANIMAL_TYPES).find(key => ANIMAL_TYPES[key].id === preyTypeId) || 'Unknown Prey';

    console.log(`[PREDATOR] Searching for ${preyTypeName}:
        Search Position: (${predatorX.toFixed(1)}, ${predatorY.toFixed(1)})
        Max Search Distance: ${MAX_SEARCH_DISTANCE}
    `);

    for (const eid of world.entities) {
        if (Animal.type[eid] === preyTypeId) {
            const targetX = Position.x[eid];
            const targetY = Position.y[eid];
            const distance = Math.sqrt(
                Math.pow(targetX - predatorX, 2) + 
                Math.pow(targetY - predatorY, 2)
            );

            if (distance < MAX_SEARCH_DISTANCE && distance < minDistance) {
                minDistance = distance;
                nearestTarget = eid;
                console.log(`[PREDATOR] Found ${preyTypeName} #${eid} at distance ${distance.toFixed(1)}`);
            }
        }
    }

    if (nearestTarget) {
        console.log(`[PREDATOR] Selected nearest ${preyTypeName} at distance: ${minDistance.toFixed(1)}`);
    } else {
        console.log(`[PREDATOR] No ${preyTypeName} found within search radius`);
    }

    return nearestTarget;
}

// Helper function to eat a target animal
export function eatTargetAnimal(world, predatorId, targetId) {
    const targetTypeId = Animal.type[targetId];
    const targetTypeName = Object.keys(ANIMAL_TYPES).find(key => ANIMAL_TYPES[key].id === targetTypeId) || 'Unknown Target';
    console.log(`[PREDATOR] Predator #${predatorId} attempting to eat ${targetTypeName} #${targetId}`);
    console.log(`    Target Type ID: ${targetTypeId}`);
    console.log(`    Is in entities array: ${world.entities.includes(targetId)}`);

    // Verify the target is a valid animal
    if (!targetTypeId || !ANIMAL_TYPES[targetTypeName]) {
        console.log(`[PREDATOR] Cannot eat target - invalid type or not an animal.`);
        return false;
    }

    // --- Standard Entity Removal Process ---
    // 1. Remove sprite if it exists
    if (world.sprites && world.sprites[targetId]) {
        console.log(`[PREDATOR] Removing ${targetTypeName} sprite`);
        world.sprites[targetId].destroy();
        delete world.sprites[targetId];
    }
    
    // 2. Remove from ECS World (should handle component removal)
    if (world.removeEntity) {
        console.log(`[PREDATOR] Removing ${targetTypeName} from ECS world`);
        world.removeEntity(targetId);
    } else {
        console.warn("[PREDATOR] world.removeEntity function not found!");
        // Manual component removal as fallback (less ideal)
        // delete Position[targetId]; delete Animal[targetId]; delete AI[targetId]; etc.
    }
    
    // 3. Remove from world entities array (if used)
    const targetIndex = world.entities?.indexOf(targetId);
    if (targetIndex !== -1 && world.entities) {
        console.log(`[PREDATOR] Removing ${targetTypeName} from entities array at index ${targetIndex}`);
        world.entities.splice(targetIndex, 1);
    }
    // --- End Removal Process ---
    
    console.log(`[PREDATOR] Successfully removed ${targetTypeName} #${targetId} from world`);
    return true;
} 